import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

class HorizontalNav extends Component {
  constructor() {
    super();
    // Pre-bind functions to keep consistent references for removal
    this._onScroll = this.#handleScroll.bind(this);
    this._onMediaQueryChange = this.#handleMediaQueryChange.bind(this);
    this._onOutsideClick = this.#handleOutsideClick.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    
    this.details = this.querySelector('[data-horizontal-nav-details]');
    this.toggle = this.querySelector('[data-horizontal-nav-toggle]');
    this.list = this.querySelector('[data-horizontal-nav-list]');
    this.currentLabel = this.querySelector('[data-current-label]');
    this.links = this.querySelectorAll('.horizontal-nav__link');
    
    this.#updateActiveState();
    
    window.addEventListener('scroll', this._onScroll, { passive: true });
    mediaQueryLarge.addEventListener('change', this._onMediaQueryChange);
    document.addEventListener('click', this._onOutsideClick);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    mediaQueryLarge.removeEventListener('change', this._onMediaQueryChange);
    document.removeEventListener('click', this._onOutsideClick);
    super.disconnectedCallback();
  }

  toggleMenu() {
    // details/summary native behavior is mostly self-sufficient
  }

  /**
   * Dropdown item selection handler
   * @param {any} event
   */
  selectItem(event) {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    event.preventDefault();

    const title = event.currentTarget.getAttribute('data-title') || '';

    // Close the dropdown
    if (this.details) {
      this.details.removeAttribute('open');
    }

    // Smooth scroll to target
    this.scrollToTarget(event);

    // Update label (mobile only)
    if (this.currentLabel && isMobileBreakpoint()) {
      this.currentLabel.textContent = title;
    }
  }

  /**
   * Smooth scroll to section target
   * @param {any} event
   */
  scrollToTarget(event) {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    const href = event.currentTarget.getAttribute('href') || '';
    if (!href || !href.includes('#')) return;

    // Support both anchor-only and path#anchor
    const targetId = href.split('#')[1] || '';
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      event.preventDefault();
      
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const navHeight = this.offsetHeight || 0;
      // Subtract heights to prevent element being covered
      const offset = headerHeight + navHeight + 10; 

      const top = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    // Update active class immediately
    this.querySelectorAll('[data-nav-item]').forEach(li => li.classList.remove('is-current'));
    event.currentTarget.closest('[data-nav-item]')?.classList.add('is-current');
  }

  #handleScroll() {
    this.#updateActiveState();
  }

  #handleMediaQueryChange() {
    if (!isMobileBreakpoint() && this.details) {
      this.details.removeAttribute('open');
    }
  }

  /**
   * @param {any} event
   */
  #handleOutsideClick(event) {
    if (this.details && !this.contains(event.target)) {
      this.details.removeAttribute('open');
    }
  }

  #updateActiveState() {
    let currentSectionId = '';
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const navHeight = this.offsetHeight || 0;
    const offset = headerHeight + navHeight + 120; // Tolerance for highlight

    if (this.links) {
      this.links.forEach(link => {
        const href = link.getAttribute('href') || '';
        if (href && href.includes('#')) {
          const targetId = href.split('#')[1] || '';
          const el = document.getElementById(targetId);
          if (el && el.getBoundingClientRect().top <= offset) {
            currentSectionId = targetId;
          }
        }
      });

      this.links.forEach(link => {
        const item = link.closest('[data-nav-item]');
        const href = link.getAttribute('href') || '';
        const isActive = href && currentSectionId !== '' && href.endsWith(`#${currentSectionId}`);

        if (isActive) {
          item?.classList.add('is-current');
          if (this.currentLabel && isMobileBreakpoint()) {
            this.currentLabel.textContent = link.getAttribute('data-title') || '';
          }
        } else {
          item?.classList.remove('is-current');
        }
      });
    }
  }
}

if (!customElements.get('horizontal-nav-component')) {
  customElements.define('horizontal-nav-component', HorizontalNav);
}
